import QRCodeStyling from 'qr-code-styling'
import { buildBrandedQrOptions, type BuildBrandedQrArgs } from '@/lib/brandedQrOptions'

/**
 * Branded QR codes — browser-side rendering.
 *
 * The decision logic (error correction, logo sizing, the min-size gate) lives
 * in `brandedQrOptions.ts` with a type-only dependency on qr-code-styling, so
 * it is unit-testable in the default node test environment. Importing the
 * library as a *value* pulls in jsdom/canvas, which hangs a plain node test
 * run — hence the split. Everything below genuinely needs a DOM.
 */

export * from '@/lib/brandedQrOptions'

export function buildBrandedQr(args: BuildBrandedQrArgs): QRCodeStyling {
  return new QRCodeStyling(buildBrandedQrOptions(args))
}

// ─────────────────────────────────────────────────────────────────────────
// Logo loading
// ─────────────────────────────────────────────────────────────────────────

const logoCache = new Map<string, Promise<string | null>>()

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(new Error('Failed to read image blob'))
    reader.readAsDataURL(blob)
  })
}

/**
 * Resolve `receipt_logo_url` to a data: URL suitable for drawing into a QR.
 *
 * Why a data URL and not the URL itself: qr-code-styling draws the logo onto a
 * canvas and we then call `getRawData()` / `toDataURL()` on it. A cross-origin
 * image taints the canvas and those calls throw SecurityError. Fetching to a
 * blob and inlining sidesteps that entirely — the same technique posterExport
 * already uses in `tryLoadImage`, and the reason no `crossOrigin` attribute
 * appears anywhere in this codebase.
 *
 * The setting arrives in one of two shapes, both fine here:
 *   - a Cloudinary URL (res.cloudinary.com sends `Access-Control-Allow-Origin: *`)
 *   - an inline `data:` URI, when no CDN is configured
 * A tenant-pasted arbitrary external URL is the only case that can fail, and
 * failure returns null so the caller falls back to a plain QR.
 */
export async function loadBrandLogo(logoUrl: string | null | undefined): Promise<string | null> {
  const src = (logoUrl || '').trim()
  if (!src) return null
  if (src.startsWith('data:')) return src
  if (!/^https?:\/\//i.test(src)) return null

  const cached = logoCache.get(src)
  if (cached) return cached

  const task = (async () => {
    try {
      const res = await fetch(src, { mode: 'cors' })
      if (!res.ok) return null
      return await blobToDataUrl(await res.blob())
    } catch {
      return null
    }
  })()

  logoCache.set(src, task)
  return task
}

/** Test seam / settings-change hook. */
export function clearBrandLogoCache(): void {
  logoCache.clear()
}

/**
 * Threshold a logo to pure black and white for thermal receipt printing.
 *
 * Receipt printers are 1-bit: every pixel is ink or no ink, and the driver
 * dithers anything in between. Dithering a colour logo inside a QR scatters
 * stray dots across modules the decoder is trying to read, which is the
 * failure mode that makes "logo on a receipt QR" a bad idea by default.
 * Collapsing to pure #000/#fff first means what prints is what we intended.
 *
 * Transparent pixels become white so a transparent-background PNG doesn't
 * threshold into a solid black square.
 */
export async function toMonochromeDataUrl(dataUrl: string, threshold = 0.62): Promise<string> {
  try {
    const img = await loadImageElement(dataUrl)
    const canvas = document.createElement('canvas')
    canvas.width = img.naturalWidth || img.width
    canvas.height = img.naturalHeight || img.height
    if (!canvas.width || !canvas.height) return dataUrl

    const ctx = canvas.getContext('2d')
    if (!ctx) return dataUrl
    ctx.drawImage(img, 0, 0)

    const image = ctx.getImageData(0, 0, canvas.width, canvas.height)
    const px = image.data
    for (let i = 0; i < px.length; i += 4) {
      const alpha = px[i + 3] / 255
      // Composite against white before thresholding.
      const r = px[i] * alpha + 255 * (1 - alpha)
      const g = px[i + 1] * alpha + 255 * (1 - alpha)
      const b = px[i + 2] * alpha + 255 * (1 - alpha)
      const luma = (0.299 * r + 0.587 * g + 0.114 * b) / 255
      const v = luma < threshold ? 0 : 255
      px[i] = px[i + 1] = px[i + 2] = v
      px[i + 3] = 255
    }
    ctx.putImageData(image, 0, 0)
    return canvas.toDataURL('image/png')
  } catch {
    return dataUrl
  }
}

function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`))
    img.src = src
  })
}

/**
 * Render to a data: URL, for the consumers that build print HTML as a string
 * (thermal receipts, the table-QR print popup) rather than mounting a canvas.
 */
export async function renderBrandedQrDataUrl(args: BuildBrandedQrArgs): Promise<string> {
  const qr = buildBrandedQr(args)
  const blob = await qr.getRawData('png')
  if (!blob) throw new Error('QR render produced no data')
  return blobToDataUrl(blob as Blob)
}
